import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Stethoscope, FileText,
  Wallet, PiggyBank, Package, UserCog, FileBarChart2, LogOut, Menu, X, Languages, Crown, Sparkles, BookOpen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import SubscriptionBanner from "../components/SubscriptionBanner";

const ROLE_NAV = {
  owner: ["dashboard", "insights", "patients", "appointments", "consultations", "invoices", "payments", "cash", "stock", "users", "reports", "documentation", "billing"],
  admin: ["dashboard", "insights", "patients", "appointments", "consultations", "invoices", "payments", "cash", "stock", "users", "reports", "documentation"],
  doctor: ["dashboard", "patients", "appointments", "consultations", "stock", "documentation"],
  nurse: ["dashboard", "patients", "appointments", "consultations", "stock", "documentation"],
  secretary: ["dashboard", "patients", "appointments", "invoices", "payments", "documentation"],
  cashier: ["dashboard", "invoices", "payments", "cash", "documentation"],
  stock_manager: ["dashboard", "stock", "documentation"],
};

const NAV_ICONS = {
  dashboard: LayoutDashboard, insights: Sparkles, patients: Users, appointments: CalendarDays,
  consultations: Stethoscope, invoices: FileText, payments: Wallet,
  cash: PiggyBank, stock: Package, users: UserCog, reports: FileBarChart2,
  billing: Crown, documentation: BookOpen,
};

const NAV_PATHS = {
  dashboard: "/app", insights: "/app/insights",
  patients: "/app/patients", appointments: "/app/appointments",
  consultations: "/app/consultations", invoices: "/app/invoices", payments: "/app/payments",
  cash: "/app/cash", stock: "/app/stock", users: "/app/users", reports: "/app/reports",
  billing: "/app/billing", documentation: "/app/documentation",
};

export default function DashboardLayout({ children }) {
  const { user, tenant, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);

  // Close drawer when resizing to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!user) return null;
  const items = ROLE_NAV[user.role] || ["dashboard"];

  return (
    <div className="min-h-screen shell-gradient">
      {/* Overlay for mobile */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Unified sidebar (single instance). Hidden off-screen on mobile, always visible on desktop. */}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-brand text-white flex flex-col transform transition-transform duration-300 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="MediFlow Africa"
              className="w-11 h-11 rounded-xl object-cover bg-[#3a3a3a] shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="text-white font-heading font-bold text-lg leading-tight truncate">
                {tenant?.name || "MediFlow"}
              </div>
              <div className="text-white/60 text-xs truncate">{t("app.name")}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10"
              data-testid="sidebar-close"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 flex flex-col gap-1 px-3">
          {items.map((key) => {
            const Icon = NAV_ICONS[key];
            return (
              <NavLink
                key={key}
                to={NAV_PATHS[key]}
                end={key === "dashboard"}
                onClick={() => setOpen(false)}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-white/75 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={2} />
                <span>{t(`nav.${key}`)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white font-heading font-semibold text-sm">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm font-semibold truncate">
                {user.first_name} {user.last_name}
              </div>
              <div className="text-white/60 text-xs truncate">{t(`roles.${user.role}`)}</div>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            data-testid="lang-toggle"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
          >
            <Languages className="w-4 h-4" />
            {lang === "fr" ? "Français" : "English"} · {lang === "fr" ? "EN" : "FR"}
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white text-sm font-semibold transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile top header */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[var(--border-default)] flex items-center justify-between px-4 h-16">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="sidebar-open"
            aria-label="Open menu"
            className="p-2 -ml-2 rounded-xl hover:bg-subtle"
          >
            <Menu className="w-6 h-6 text-ink" />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="MediFlow Africa"
              className="w-9 h-9 rounded-lg object-cover bg-[#3a3a3a] shrink-0"
            />
            <div className="font-heading font-bold text-ink truncate max-w-[160px]">
              {tenant?.name || "MediFlow"}
            </div>
          </div>
        </div>
        <button
          onClick={() => setLang(lang === "fr" ? "en" : "fr")}
          data-testid="lang-toggle-mobile"
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-subtle text-ink text-sm font-semibold"
        >
          <Languages className="w-4 h-4" />
          {lang.toUpperCase()}
        </button>
      </div>

      <main className="lg:pl-72">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          <SubscriptionBanner />
          {children}
        </div>
      </main>
    </div>
  );
}
